import { STORE_KEYS } from '@constants'
import { saveToStorage } from '@helpers/storage.helpers'
import { Store } from '@stores'
import {
  IEmailTemplate,
  IEmailLog,
  IEmailLogWithRelations,
  ISendEmailRequest,
  IEmailTemplateCreateRequest,
  IEmailTemplateUpdateRequest,
  IEmailLogsFilters,
  ISingleDocument,
} from '@typings'
import { action, computed, makeAutoObservable, runInAction } from 'mobx'

const defaultValues: Record<string, any> = {
  loading: false,
  emailTemplates: [],
  emailTemplate: {},
  emailLogs: [],
  emailLog: {},
}

export class EmailStore {
  [key: string]: any
  loading: boolean
  emailTemplates: IEmailTemplate[]
  emailTemplate: IEmailTemplate
  emailLogs: IEmailLog[]
  emailLog: IEmailLogWithRelations
  store: Store

  constructor(store: Store) {
    makeAutoObservable(this)
    this.store = store
    this.loading = defaultValues.loading
    this.emailTemplates = defaultValues.emailTemplates
    this.emailTemplate = defaultValues.emailTemplate
    this.emailLogs = defaultValues.emailLogs
    this.emailLog = defaultValues.emailLog
  }

  @computed
  get current_template(): IEmailTemplate {
    return this.emailTemplate
  }

  @computed
  get templates_list(): IEmailTemplate[] {
    return this.emailTemplates
  }

  @computed
  get logs_list(): IEmailLog[] {
    return this.emailLogs
  }

  // Email Templates
  @action
  getTemplates = async (params?: any) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const { data } = await this.store.api.email.getTemplates(params)
      this.set(STORE_KEYS.EMAIL_TEMPLATES, data)
      return Promise.resolve(data)
    } catch (e) {
      console.error('Error fetching email templates:', e)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getTemplate = async (id: IEmailTemplate['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.email.getTemplate(id)
      this.set(STORE_KEYS.EMAIL_TEMPLATE, result)
      return Promise.resolve(result)
    } catch (e) {
      console.error('Error fetching email template:', e)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  createTemplate = async (data: IEmailTemplateCreateRequest) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      if (!this.store.company.current_company_id) {
        await this.store.company.getMyCompany()
      }

      if (!this.store.company.current_company_id) {
        throw new Error('Company ID is required to create email template')
      }

      const _data: IEmailTemplateCreateRequest = {
        ...data,
        company_id: this.store.company.current_company_id,
      }

      const result = await this.store.api.email.createTemplate(_data)
      await this.getTemplates()
      return Promise.resolve(result)
    } catch (e: any) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  updateTemplate = async (id: IEmailTemplate['id'], data: IEmailTemplateUpdateRequest) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.email.updateTemplate(id, data)
      this.set(STORE_KEYS.EMAIL_TEMPLATE, result)
      await this.getTemplates()
      return Promise.resolve(result)
    } catch (e: any) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  deleteTemplate = async (id: IEmailTemplate['id']) => {
    await this.store.ui.confirm({
      title: 'E-mailsjabloon verwijderen?',
      content: 'Je staat op het punt dit e-mailsjabloon te verwijderen. Weet je het zeker?',
      withCancel: true,
    })

    this.set(STORE_KEYS.LOADING, true)
    try {
      await this.store.api.email.deleteTemplate(id)
      await this.getTemplates()
      return Promise.resolve()
    } catch (e) {
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getDefaultTemplate = async (type: string) => {
    try {
      const result = await this.store.api.email.getDefaultTemplate(type)
      return Promise.resolve(result)
    } catch (e) {
      console.error('Error fetching default template:', e)
      return Promise.reject(e)
    }
  }

  // Email Logs
  @action
  getLogs = async (filters?: IEmailLogsFilters) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const { data } = await this.store.api.email.getLogs(filters)
      this.set(STORE_KEYS.EMAIL_LOGS, data)
      return Promise.resolve(data)
    } catch (e) {
      console.error('Error fetching email logs:', e)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  getLog = async (id: IEmailLog['id']) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.email.getLog(id)
      this.set(STORE_KEYS.EMAIL_LOG, result)
      return Promise.resolve(result)
    } catch (e) {
      console.error('Error fetching email log:', e)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  // Send Email
  @action
  sendDocumentEmail = async (documentId: ISingleDocument['id'], data: ISendEmailRequest) => {
    this.set(STORE_KEYS.LOADING, true)
    try {
      const result = await this.store.api.email.sendDocumentEmail(documentId, data)
      this.store.ui.handleToaster('E-mail succesvol verzonden', { type: 'success' })
      return Promise.resolve(result)
    } catch (e: any) {
      const errorMessage = e?.message || 'Er is een fout opgetreden bij het verzenden van de e-mail'
      this.store.ui.handleErrorToast(errorMessage)
      return Promise.reject(e)
    } finally {
      this.set(STORE_KEYS.LOADING, false)
    }
  }

  @action
  reset = (withLoading?: boolean) => {
    runInAction(() =>
      Object.keys(defaultValues).map(key => {
        this.set(key, defaultValues[key])
      })
    )
    if (withLoading) this.set(STORE_KEYS.LOADING, false)
  }

  @action
  set = (target: string, value: unknown, save?: boolean): unknown => {
    if (!target) return
    this[target] = value
    if (save) saveToStorage(target, value)
  }
}
